import { supabase } from '../utils/supabase';

/**
 * Synchronize tournament groups with the group_count setting
 * Handles adding new empty groups or removing excess groups
 */
export const syncTournamentGroups = async (tournamentId, newGroupCount) => {
  // Get current groups
  const { data: existingGroups, error: fetchError } = await supabase
    .from('tournament_groups')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('sort_order');

  if (fetchError) throw fetchError;

  const currentGroupCount = existingGroups?.length || 0;

  if (newGroupCount === currentGroupCount) {
    // No change needed
    return existingGroups;
  }

  if (newGroupCount > currentGroupCount) {
    // ADD new empty groups
    const { generateGroupNames } = await import('../utils/tournamentMatchGeneration');
    const allGroupNames = generateGroupNames(newGroupCount);
    const newGroups = [];

    for (let i = currentGroupCount; i < newGroupCount; i++) {
      const { data: newGroup, error: insertError } = await supabase
        .from('tournament_groups')
        .insert({
          tournament_id: tournamentId,
          name: allGroupNames[i],
          sort_order: i + 1
        })
        .select()
        .single();

      if (insertError) throw insertError;
      newGroups.push(newGroup);
    }

    return [...existingGroups, ...newGroups];
  } else {
    // REMOVE excess groups (keep first N groups)
    const groupsToKeep = existingGroups.slice(0, newGroupCount);
    const groupsToRemove = existingGroups.slice(newGroupCount);

    // Delete the excess groups (cascade will handle team assignments and matches)
    for (const group of groupsToRemove) {
      const { error: deleteError } = await supabase
        .from('tournament_groups')
        .delete()
        .eq('id', group.id);

      if (deleteError) throw deleteError;
    }

    return groupsToKeep;
  }
};

/**
 * Assign a court to a group
 * @param {string} groupId - Group ID
 * @param {number} courtNumber - Court number (or null to unassign)
 */
export const assignCourtToGroup = async (groupId, courtNumber) => {
  const { data, error } = await supabase
    .from('tournament_groups')
    .update({ assigned_court: courtNumber })
    .eq('id', groupId)
    .select()
    .single();

  if (error) throw error;
  return data;
};
